import React, { useEffect, useRef, useState } from 'react';
import { renderMathIn } from './mathliveTheme';
import { PANEL } from './panelTheme';
import { plot, toPath } from './plot';
import { usePhases } from './waitPhrases';
import {
  forDisplay,
  toMathValue,
  fromMathValue,
  strikeStyle,
  roleOf,
  MOVE_OPTIONS,
  ROLE_ORDER,
  ROLE_TITLES,
  partsOf,
} from './elementKind';

/**
 * =============================================================================
 * ProblemCard — punchlist 1.4
 * =============================================================================
 *
 * ONE card. Interpretation and answer are ONE component: a student would be
 * reading them at different times for no reason, and two toggle strips meant
 * the same colour carrying two meanings on one screen.
 *
 * ── IT INHERITS NOTHING ────────────────────────────────────────────────────
 * Every colour, size, indent and inset comes from `PANEL`. No `useTheme`, no
 * `TIER_COLORS`, no CSS variables from <body>. A literal in a style object here
 * is a BUG — it is a value nobody can find from panelTheme.js, and the point of
 * the panel being standalone is that every colour and every transparency is
 * editable in one place.
 *
 * ── THE FOUR SECTIONS ──────────────────────────────────────────────────────
 *
 *   Instructions   from the page, governing from outside. Their own array,
 *                  their own ids, no role, no dropdown.
 *   Problem        what is being asked
 *   Your work      the student's steps
 *   Your answer    what they got
 *
 * THREE INDENT LEVELS SAY THE STRUCTURE, so no colour has to. `Interpretation`
 * and `Answer` at 0, `Instructions` at 10, the three roles at 20. The step only
 * has to be legible, not generous — a heading starting 10px right of the one
 * above is unmistakably nested.
 *
 * ── THE RIGHT EDGE IS THE HARD PART ────────────────────────────────────────
 * Every boundary is a FIXED INSET from the card's content edge, never a
 * percentage. A percentage of an indented container resolves somewhere else
 * than the same percentage of its parent, so a 79% clip at the 10px level and
 * a 79% clip at the 20px level end at two different places.
 *
 *   R              content lines, headings, edit, Resubmit
 *   R - gutter     EVERY editable field, instruction and role alike
 *   R - notes      where instructions stop in display
 *
 * ── WHERE EVERY VALUE COMES FROM ───────────────────────────────────────────
 * This component owns no state except which of its own sections are open.
 *
 *   status, answer            <- derive(log, drafts), via the panel
 *   content when NOT editing  <- state.problems[label].problem
 *   content WHEN editing      <- drafts[label]   ← A DIFFERENT STORE
 *
 * THE TWO-STORE READ IS THE WHOLE DESIGN. A draft is a full copy made when
 * `edit` is pressed, so while one exists it is the complete current version and
 * `problem` is only the record of what was SENT. Merging them would make
 * `problem` change on every keystroke, and anything watching it would fire on
 * every character.
 * =============================================================================
 */

const L = PANEL.layout;

// HOW MANY INSTRUCTIONS THE DISPLAY SHOWS. A VIEW LIMIT AND NOTHING ELSE — the
// problem keeps every instruction it has, the editor shows all of them, and all
// of them travel on a resubmit. A worksheet can carry a title, a directive, a
// note about calculators and a note about credit, and four of those above one
// problem is a wall of text the student did not write and cannot act on.
const MAX_SHOWN_INSTRUCTIONS = 3;

// ── responsive ──────────────────────────────────────────────────────────────
// Below this the inline editor row leaves the field too narrow to see an
// expression in, so the indents halve and the role labels shorten.
function useNarrow() {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < PANEL.narrowAt : false
  );
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < PANEL.narrowAt);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return narrow;
}

// ── one displayed line ──────────────────────────────────────────────────────
//
// Everything that reads `kind` goes through ./elementKind. This component never
// compares `kind` itself — see the note at the top of that module for why that
// matters when the transcriber prompt is fixed.
/**
 * A table, a plot or a figure.
 *
 * ── ONE CONDITION DECIDES EVERYTHING ─────────────────────────────────────────
 *
 *   draw  =  there is data, and it was not refused
 *
 * That covers all four states of a graphic without a switch:
 *
 *   a table                 data present   ->  a grid
 *   blank axes              data, curves [] ->  an empty grid  (STAGE F)
 *   a plot with a spec      data present   ->  a curve        (STAGE F)
 *   a plot not yet read     no data        ->  the dashed box
 *   a plot the pass refused data.unsupported -> the dashed box
 *   a figure                no data, ever  ->  the dashed box
 *
 * A FIGURE AND A FAILED PLOT LOOK IDENTICAL, deliberately. One fallback, one
 * appearance, and the description is never lost — it is what the model read and
 * what the answer was built from.
 *
 * ── COLOUR ───────────────────────────────────────────────────────────────────
 *
 * Printed content is the card's NORMAL text. Only HANDWRITTEN is marked, in the
 * green this card already uses for the student's own work — so there is ONE
 * thing to explain, and a graphic with nothing handwritten gets no colour and
 * no legend at all.
 *
 * That also makes a misread visible for free: a printed value showing green
 * means we thought the student wrote it, and it will be GRADED rather than
 * USED.
 */
function Graphic({ element, narrow }) {
  // NO EARLY RETURN ON A MISSING `graphic`. The transcriber can mark an element
  // as a graphic and omit the object — the parse turns that into `graphic:
  // null`, and returning null here would render NOTHING where a description
  // belongs. It falls through to the dashed box instead, which is the same
  // fallback a figure uses and is always correct: `content` is never empty.
  const g = (element && element.graphic) || {};

  const inset = narrow ? L.stepsInsetMobile : L.stepsInset;
  const own = element.origin === 'handwritten';
  const ink = own ? PANEL.successText : PANEL.text;

  // THE ONE CONDITION. `curves: []` is still data — blank axes DRAW, as an
  // empty grid, because they are on the page.
  const drawable = !!g.data && !g.data.unsupported;

  const box = {
    marginLeft: L.indentBody,
    marginRight: inset,
    marginTop: 4,
    padding: '10px 12px',
    borderRadius: 8,
    background: PANEL.sunken,
    border: `0.5px ${drawable ? 'solid' : 'dashed'} ${PANEL.border}`,
  };

  // NOTHING TO DRAW — the description, in a dashed box. Not a failure state:
  // a figure is ALWAYS this, by design.
  if (!drawable) {
    return (
      <div style={box}>
        <p style={{ margin: 0, fontFamily: PANEL.sans, fontSize: PANEL.size.hint,
          lineHeight: 1.55, color: own ? PANEL.successText : PANEL.muted }}>
          {element.content}
        </p>
        {own && <Legend />}
      </div>
    );
  }

  if (g.type === 'table') {
    const rows = (g.data.cells || []);
    return (
      <div style={box}>
        {/* NO STYLING THAT ASSERTS STRUCTURE. No header row, no first-column
            emphasis, no striping — a multiplication table has no header and a
            magic square has no labels. Colour marks ORIGIN, which is something
            we READ; a bold first row would claim something we INVENTED. */}
        {/* ── A WIDE TABLE SCROLLS, IT DOES NOT SPILL ───────────────────────
            Cells are `nowrap` because a wrapped number is harder to read than a
            scrolled one, so a nine-column table comes to roughly 585px inside a
            340px card. WITHOUT THIS WRAPPER IT SIMPLY OVERFLOWED, and the last
            columns were unreachable.

            THE WRAPPER GOES ROUND THE TABLE, NOT THE BOX, so the legend and the
            padding stay put and only the grid moves. */}
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ borderCollapse: 'collapse',
            fontFamily: PANEL.serif, fontSize: PANEL.size.field }}>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {(row || []).map((cell, c) => (
                    // 14px EACH SIDE put 28px of padding on every column. At
                    // nine columns that is 250px of nothing, on a 340px card.
                    // 9px still separates the values and buys back ~90px.
                    <td key={c} style={{ border: `0.5px solid ${PANEL.border}`,
                      padding: '4px 9px', color: ink, whiteSpace: 'nowrap' }}>
                      {/* A null cell belongs to the OTHER half of a pair. An
                          empty string is a box empty ON THE PAGE. Both render
                          blank; only the data tells them apart. */}
                      {cell == null ? '' : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {own && <Legend />}
      </div>
    );
  }

  // ── A PLOT ────────────────────────────────────────────────────────────────
  //
  // `plot()` returns GEOMETRY ONLY — no colours, no elements. The INK is
  // decided here, from the element's `origin`, so a student's own drawn curve
  // is green while printed axes are not, and `plot.js` stays pure.
  //
  // A window that cannot be drawn — degenerate, or not four finite numbers —
  // returns null, and the description takes over. Same fallback as everything
  // else.
  const W = narrow ? 300 : 340;
  const H = Math.round(W * 0.56);
  const fig = plot(g.data, W, H);
  if (!fig) {
    return (
      <div style={{ ...box, borderStyle: 'dashed' }}>
        <p style={{ margin: 0, fontFamily: PANEL.sans, fontSize: PANEL.size.hint,
          lineHeight: 1.55, color: own ? PANEL.successText : PANEL.muted }}>
          {element.content}
        </p>
        {own && <Legend />}
      </div>
    );
  }

  const { vp, grid: gr, paths, dots } = fig;
  const PAD = 16;                          // room for the tick labels
  const clip = `plotclip-${g.id || 'g'}-${element.label || ''}`;
  const label = (n) => (Math.abs(n) < 1e-9 ? '0' : String(Math.round(n * 100) / 100));

  return (
    <div style={box}>
      <svg viewBox={`${-PAD * 2} ${-PAD} ${W + PAD * 3} ${H + PAD * 2}`}
        style={{ width: '100%', display: 'block' }} role="img"
        aria-label={element.content || 'a graph'}>
        <defs>
          {/* CLIPPED TO THE VIEWPORT. The sampler produces points beyond the
              window on purpose — the off-screen rule stops REFINEMENT, not the
              points — so a curve running off the top leaves the picture at the
              edge instead of being drawn outside the box. */}
          <clipPath id={clip}><rect x="0" y="0" width={W} height={H} /></clipPath>
        </defs>

        {/* THE GRID MATTERS MOST WHEN THERE IS NOTHING ON IT. An empty box with
            two lines through it looks broken; an empty GRID looks like the
            graph paper printed on the page. */}
        <g stroke={PANEL.border} strokeWidth="0.4" opacity="0.55">
          {gr.v.map((l) => <line key={`v${l.value}`} x1={l.px} y1="0" x2={l.px} y2={H} />)}
          {gr.h.map((l) => <line key={`h${l.value}`} x1="0" y1={l.py} x2={W} y2={l.py} />)}
        </g>

        {/* A WINDOW THAT DOES NOT CONTAIN ZERO HAS NO AXIS TO DRAW — common on
            a page showing only the first quadrant. */}
        <g stroke={PANEL.secondary} strokeWidth="1.1">
          {gr.axisX !== null && <line x1="0" y1={gr.axisX} x2={W} y2={gr.axisX} />}
          {gr.axisY !== null && <line x1={gr.axisY} y1="0" x2={gr.axisY} y2={H} />}
        </g>

        <g fontFamily={PANEL.sans} fontSize="8" fill={PANEL.muted}>
          {gr.v.filter((_, i) => i % 2 === 0).map((l) => (
            <text key={`vt${l.value}`} x={l.px} y={(gr.axisX ?? H) + 10} textAnchor="middle">
              {label(l.value)}
            </text>
          ))}
          {gr.h.filter((_, i) => i % 2 === 0).map((l) => (
            <text key={`ht${l.value}`} x={(gr.axisY ?? 0) - 5} y={l.py + 3} textAnchor="end">
              {label(l.value)}
            </text>
          ))}
        </g>

        <g clipPath={`url(#${clip})`} fill="none" stroke={ink} strokeWidth="1.9"
          strokeLinejoin="round" strokeLinecap="round">
          {paths.map((d, i) => <path key={i} d={d} />)}
        </g>

        {/* ENDPOINT DOTS, STRAIGHT FROM THE STRICTNESS. Filled for a closed
            end, hollow for an open one — no limits taken, nothing inferred.
            This is the case a plotting library makes you hand-place. */}
        <g clipPath={`url(#${clip})`}>
          {dots.map((d, i) => (
            <circle key={i} cx={d.cx} cy={d.cy} r="3"
              fill={d.filled ? ink : PANEL.sunken}
              stroke={ink} strokeWidth={d.filled ? 0 : 1.6} />
          ))}
        </g>
      </svg>
      {/* ── THE EQUATIONS, UNDER THE GRAPH ────────────────────────────────
          What the model read the curve to BE, in the form a textbook writes.
          Display only — never parsed, never computed with — so a wrong one
          sits visibly beside a curve that is right.

          THE DRAWING COMES FROM THE POINTS, NOT FROM THESE. They are two
          independent readings of the same picture, and showing both lets a
          student notice when they disagree. */}
      {Array.isArray(g.equations) && g.equations.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {g.equations.map((eq, i) => (
            <div key={i} style={{ fontFamily: PANEL.serif, fontSize: PANEL.size.hint,
              color: own ? PANEL.successText : PANEL.secondary }}>
              {eq.equation}
              <span style={{ color: PANEL.muted, marginLeft: 8 }}>
                {/* The interval is what makes a piecewise equation readable. */}
                {eq.closedLeft === false ? '(' : '['}{eq.from}, {eq.to}{eq.closedRight === false ? ')' : ']'}
              </span>
            </div>
          ))}
        </div>
      )}
      {own && <Legend />}
    </div>
  );
}

/**
 * ONE LINE, ONE THING. Printed content looks like every other printed line on
 * the card, so there is nothing to say about it.
 */
function Legend() {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 9,
      fontFamily: PANEL.sans, fontSize: 10.5, color: PANEL.muted }}>
      <span style={{ width: 9, height: 9, borderRadius: 2,
        background: PANEL.successText }} />
      what you wrote
    </div>
  );
}

function MathLine({ element, size, narrow, centre }) {
  const ref = useRef(null);

  useEffect(() => {
    renderMathIn(ref.current, forDisplay(element));
  }, [element]);

  return (
    <div
      ref={ref}
      style={{
        fontSize: size || (narrow ? PANEL.size.lineMobile : PANEL.size.line),
        lineHeight: 1.75,
        color: PANEL.text,
        margin: '0 0 2px',
        textAlign: centre ? 'center' : undefined,
        // Runs to R. A long problem statement clips rather than wrapping, so
        // the block stays one line per element and the section keeps its shape.
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...(strikeStyle(element) || {}),
      }}
    />
  );
}

// ── the loading bar ─────────────────────────────────────────────────────────
//
// TWO STYLES. `sweep` is a band travelling an empty track — indeterminate, it
// claims nothing about how far along. `fill` is the track filling rapidly and
// repeatedly, used where several classifier calls are in flight at once and the
// interpretation is already on screen to read.
function Bar({ style }) {
  return (
    <>
      <style>{`
        @keyframes pcSweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        @keyframes pcFill  { 0% { width: 4%; } 70% { width: 96%; } 100% { width: 96%; opacity: 0.35; } }
        @keyframes pcFade  { from { opacity: 0; } to { opacity: 1; } }
        .pc-phrase { animation: pcFade 0.5s ease both; }
        @media (prefers-reduced-motion: reduce) {
          .pc-bar { animation: none !important; width: 40% !important; }
          .pc-phrase { animation: none; }
        }
      `}</style>
      <div style={{ height: 3, background: PANEL.sunken, borderRadius: 2, overflow: 'hidden' }}>
        <div
          className="pc-bar"
          style={{
            height: '100%',
            borderRadius: 2,
            background: PANEL.accent,
            ...(style === 'fill'
              ? { width: '4%', animation: 'pcFill 1s ease-out infinite' }
              : { width: '32%', animation: 'pcSweep 2s ease-in-out infinite' }),
          }}
        />
      </div>
    </>
  );
}

// ── small parts ─────────────────────────────────────────────────────────────

const Heading = ({ children }) => (
  <p style={{
    fontSize: PANEL.size.heading,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 600,
    color: PANEL.blue,
    margin: '0 0 5px',
    fontFamily: PANEL.sans,
  }}>
    {children}
  </p>
);

const Hint = ({ children }) => (
  <p style={{
    fontSize: PANEL.size.hint,
    color: PANEL.muted,
    margin: '3px 0 4px',
    lineHeight: 1.5,
    fontFamily: PANEL.sans,
  }}>
    {children}
  </p>
);

function Btn({ children, kind = 'plain', onClick, label, style }) {
  // GREEN FOR `edit`, GOLD FOR `Resubmit`. One opens something, one sends
  // something, and the colour is what says so. Neither ever marks a
  // transcribed line, so neither can be read as a verdict on the work.
  const kinds = {
    plain: { fontSize: PANEL.size.control, padding: '3px 12px',
             background: 'transparent', color: PANEL.blue,
             border: `0.5px solid ${PANEL.blueBorder}` },
    grey:  { fontSize: PANEL.size.control, padding: '3px 12px',
             background: 'transparent', color: PANEL.secondary,
             border: `0.5px solid ${PANEL.border}` },
    edit:  { fontSize: PANEL.size.control, padding: '3px 12px',
             background: PANEL.successBg, color: PANEL.successText,
             border: `0.5px solid ${PANEL.successBorder}` },
    resub: { fontSize: PANEL.size.resubmit, padding: '5px 15px',
             background: PANEL.accent, color: PANEL.sunken,
             border: `0.5px solid ${PANEL.accent}`, fontWeight: 500 },
  };
  return (
    <button type="button" onClick={onClick} aria-label={label}
      style={{ ...kinds[kind], borderRadius: L.radius, cursor: 'pointer',
        fontFamily: PANEL.sans, ...style }}>
      {children}
    </button>
  );
}

// The delete glyph. A fixed width, because it is what defines the gutter that
// every field's right edge stops at.
const Del = ({ onClick, label }) => (
  <button type="button" onClick={onClick} aria-label={label}
    style={{
      flex: `0 0 ${L.deleteSize}px`, height: L.fieldHeight,
      border: 'none', background: 'transparent', color: PANEL.muted,
      cursor: 'pointer', fontSize: PANEL.size.field, lineHeight: 1, padding: 0,
    }}>
    ×
  </button>
);

// Filled = viewing. Tinted = editing or pending. Plain = answered.
// A SINGLE PROBLEM GETS NO STRIP — one lone button invites a click that does
// nothing.
function Toggles({ labels, statusFor, current, onSelect }) {
  if (labels.length < 2) return null;
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {labels.map((l) => {
        const on = l === current;
        const unfinished = ['editing', 'pending'].includes(statusFor(l));
        return (
          <button key={l} type="button" onClick={() => onSelect(l)} aria-current={on}
            style={{
              fontSize: PANEL.size.toggle, padding: '2px 9px', borderRadius: L.radius,
              cursor: 'pointer', fontFamily: PANEL.sans,
              background: on ? PANEL.accent : unfinished ? PANEL.accentBg : PANEL.surface,
              color: on ? PANEL.sunken : unfinished ? PANEL.accentText : PANEL.secondary,
              border: `0.5px solid ${on ? PANEL.accent : PANEL.border}`,
            }}>
            {l}
          </button>
        );
      })}
    </div>
  );
}

// A section of the interpretation. `deep` puts it at the role level.
//
// DEFINED AT MODULE SCOPE, NOT INSIDE THE CARD. A component declared inside
// another is a NEW TYPE on every render, so React unmounts and remounts its
// whole subtree each time — and a math-field sets its value on mount only, so
// every keystroke would destroy and rebuild the field being typed into.
function Section({ title, deep, indentSection, indentRole, children }) {
  return (
    <div style={{ marginLeft: deep ? indentRole : indentSection, marginTop: 14 }}>
      <Heading>{title}</Heading>
      {children}
    </div>
  );
}

// ── one editable line ───────────────────────────────────────────────────────
//
// A math-field for `kind: math`, a plain input for everything else. NOT a
// preference: a math-field treats its whole value as LaTeX, so a prose line put
// in one is set as a product of italic variables — and because typing saves
// live, whatever the field serialises back BECOMES the draft and is what the
// classifier receives on the resubmit.
/**
 * A graphic, in the editor.
 *
 * ── WHY IT IS NOT AN ElementField ────────────────────────────────────────────
 *
 * `ElementField` is a MathLive field. It reads through `toMathValue` and writes
 * through `fromMathValue`, and a graphic's content is PROSE — so a description
 * reading "Cash Flow Today ($)" came back from a round trip wrapped as one
 * enormous expression. That corruption was written into the tree on resubmit
 * and persisted.
 *
 * ── WHAT IS EDITABLE ─────────────────────────────────────────────────────────
 *
 *   a TABLE    its cells, one plain input each
 *   a FIGURE   its description, and nothing else — the description IS the
 *              representation, there is no structure to correct
 *   a PLOT     its description. The spec is coefficients and a window; asking a
 *              student to type that is not editing, it is programming. And the
 *              description is what the graph pass re-reads, so correcting it is
 *              the repair path for a bad read.
 *
 * NO ROLE DROPDOWN. A graphic's role comes from its `origin`, and a pair's two
 * halves are in different role sections by design — a dropdown would let a
 * student move the printed axes into "Your work".
 *
 * `onChange` TAKES A PATCH, which `changeElement` already spreads over the
 * element. So a cell edit is `{ graphic: … }` and a description edit is
 * `{ content }`, through the same handler the text rows use. No new plumbing.
 */
function GraphicField({ element, narrow, parts, onChange, onPart, onRemove }) {
  const g = element.graphic || {};
  const own = element.origin === 'handwritten';
  const ink = own ? PANEL.successText : PANEL.text;
  const edge = own ? PANEL.successBorder : PANEL.blueBorder;

  const cells = (g.data && g.data.cells) || null;

  const setCell = (r, c, value) => {
    const next = cells.map((row, i) =>
      (i === r ? row.map((cell, j) => (j === c ? value : cell)) : row));
    onChange({ graphic: { ...g, data: { ...g.data, cells: next } } });
  };

  const addRow = () => {
    const width = cells.length ? cells[0].length : 1;
    onChange({ graphic: { ...g, data: { ...g.data,
      cells: [...cells, Array.from({ length: width }, () => '')] } } });
  };
  const addCol = () => onChange({ graphic: { ...g, data: { ...g.data,
    cells: cells.map((row) => [...row, '']) } } });
  const dropRow = (r) => onChange({ graphic: { ...g, data: { ...g.data,
    cells: cells.filter((_, i) => i !== r) } } });
  const dropCol = (c) => onChange({ graphic: { ...g, data: { ...g.data,
    cells: cells.map((row) => row.filter((_, j) => j !== c)) } } });

  const field = {
    height: L.fieldHeight, borderRadius: L.radius, background: PANEL.bg,
    border: `0.5px solid ${edge}`, color: ink,
    fontFamily: PANEL.serif, fontSize: PANEL.size.field, padding: '0 8px',
    outline: 'none',
  };
  const kill = { width: 16, textAlign: 'center', color: PANEL.muted,
    fontSize: 13, cursor: 'pointer', userSelect: 'none' };

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        {/* WHAT IT IS. Read-only — the transcriber decided, and a student
            changing a table into a figure would discard its cells. */}
        <span style={{ flex: `0 0 ${narrow ? 62 : 78}px`, height: L.fieldHeight,
          borderRadius: L.radius, background: PANEL.sunken, color: PANEL.muted,
          border: `0.5px solid ${PANEL.border}`, fontFamily: PANEL.sans,
          fontSize: PANEL.size.role, display: 'flex', alignItems: 'center',
          justifyContent: 'center' }}>
          {g.type || 'graphic'}
        </span>

        {parts && parts.length > 0 && (
          <select value={element.label || ''} onChange={(e) => onPart(e.target.value || null)}
            aria-label="Which part this belongs to"
            style={{ flex: `0 0 ${narrow ? 58 : 74}px`, ...field,
              fontFamily: PANEL.sans, fontSize: PANEL.size.role, color: PANEL.blue }}>
            <option value="">shared</option>
            {parts.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}

        <input value={element.content || ''} onChange={(e) => onChange({ content: e.target.value })}
          aria-label="What this graphic shows"
          style={{ flex: 1, minWidth: 0, ...field }} />

        <span style={kill} onClick={onRemove} role="button" aria-label="Remove">×</span>
      </div>

      {/* THE CELLS. Plain inputs — most cells are numbers, and a grid of
          MathLive fields is twelve keyboards on one card. */}
      {cells && (
        <div style={{ marginTop: 6, marginLeft: L.indentBody, display: 'inline-block' }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 3 }}>
            {(cells[0] || []).map((_, c) => (
              <span key={c} style={{ ...kill, width: narrow ? 60 : 76 }}
                onClick={() => dropCol(c)} role="button" aria-label="Remove column">×</span>
            ))}
          </div>
          {cells.map((row, r) => (
            <div key={r} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
              {(row || []).map((cell, c) => (
                <input key={c} value={cell == null ? '' : cell}
                  onChange={(e) => setCell(r, c, e.target.value)}
                  style={{ ...field, flex: `0 0 ${narrow ? 60 : 76}px` }} />
              ))}
              <span style={kill} onClick={() => dropRow(r)} role="button" aria-label="Remove row">×</span>
              {r === 0 && (
                <span style={{ ...kill, color: PANEL.blue }} onClick={addCol}
                  role="button" aria-label="Add column">+</span>
              )}
            </div>
          ))}
          <Btn onClick={addRow}>+ row</Btn>
        </div>
      )}
    </div>
  );
}

function ElementField({ element, narrow, parts, onChange, onRole, onPart, onRemove }) {
  const mfRef = useRef(null);

  // EVERY ROLE LINE IS A MATH-FIELD, whatever its `kind`.
  //
  // The alternative — a math-field for `kind: math` and a plain input for
  // everything else — is arbitrary from the student's side: they cannot see the
  // kind, so the box would change type for reasons invisible to them, and a
  // problem statement they wanted to add a fraction to would refuse.
  //
  // `toMathValue` is what makes it safe. A math-field treats its whole value as
  // LaTeX, so prose goes in inside a \text{} group; without that the Calculus
  // problem statement is set as forty italic variables.
  useEffect(() => {
    const mf = mfRef.current;
    if (!mf) return undefined;

    // A TEXT BOX THAT KNOWS MATHS.
    //
    // `defaultMode: 'text'` makes it behave as prose by default, and
    // `smartMode` flips it into maths when maths is typed — the docs' own
    // example is typing "if x > 0". One field, no segments to tab between, no
    // caret stranded at a boundary. `alt`/`option`+`=` toggles by hand when the
    // guess is wrong.
    //
    // This replaced a branch that gave `kind: math` a math-field and everything
    // else a plain input. That was arbitrary from the student's side — they
    // cannot see `kind`, so the box changed type for invisible reasons and a
    // problem statement refused a fraction.
    mf.defaultMode = 'text';
    mf.smartMode = true;

    // Writing needs no conversion: in text mode MathLive parses `$…$` itself,
    // so the contract string goes in as it stands.
    mf.value = toMathValue(element);

    // Reading does. A field returns maths-with-text-islands; the contract keeps
    // text-with-maths-islands, because that is what the classifier reads and
    // what the transcriber emits.
    const onInput = () => onChange(fromMathValue(mf.value));
    mf.addEventListener('input', onInput);
    return () => mf.removeEventListener('input', onInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', gap: L.gap, alignItems: 'center', marginBottom: L.rowGap }}>
      {/* THE ROLE DROPDOWN LEADS THE ROW. It is the important control, not the
          text field — what a line IS decides how a resubmit uses it, and that
          is a dropdown change rather than a retype. */}
      <select
        value={roleOf(element)}
        onChange={(e) => onRole(e.target.value)}
        aria-label="What this line is"
        style={{
          flex: `0 0 ${narrow ? L.roleWidthMobile : L.roleWidth}px`,
          height: L.fieldHeight, fontSize: PANEL.size.role,
          fontFamily: PANEL.sans, borderRadius: L.radius,
          // A native select paints itself from the OS on a dark background
          // unless all three are set.
          background: PANEL.sunken,
          color: PANEL.blue,
          border: `0.5px solid ${PANEL.blueBorder}`,
        }}
      >
        {/* FOUR OPTIONS, THREE ROLES. `Instructions` is not a role — it moves
            the line into the problem's instructions array, which is a different
            store. Same gesture from the student's side, so the same control. */}
        {MOVE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{narrow ? o.short : o.label}</option>
        ))}
      </select>

      {/* WHICH PART THIS LINE BELONGS TO.
          
          THE DROPDOWN IS THE MARKER. There is no separate label beside it — the
          editor is the one place a part can be CHANGED, so showing it twice
          would be showing the same fact in a control and in text.

          ONLY WHEN THE PROBLEM HAS PARTS. An ordinary problem renders the row
          exactly as it did before, with nothing to choose between. */}
      {parts && parts.length > 0 && (
        <select
          value={element.label || ''}
          onChange={(e) => onPart(e.target.value || null)}
          aria-label="Which part this line belongs to"
          style={{
            flex: `0 0 ${narrow ? 64 : 84}px`,
            height: L.fieldHeight, fontSize: PANEL.size.role,
            fontFamily: PANEL.sans, borderRadius: L.radius,
            background: PANEL.sunken,
            color: PANEL.blue,
            border: `0.5px solid ${PANEL.blueBorder}`,
          }}
        >
          {/* THE STEM IS AN OPTION. A line can belong to the problem as a whole
              rather than to any part — that is what the shared statement is. */}
          <option value="">shared</option>
          {parts.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      )}

      {/* WHITE INK, BLUE BORDER. The blue marks the field as belonging to the
          panel; the content is the student's own line and reads in the same
          colour it does when the card is not being edited. */}
      <math-field
        ref={mfRef}
        style={{
          flex: 1, minWidth: 0, boxSizing: 'border-box',
          minHeight: L.fieldHeight,
          border: `0.5px solid ${PANEL.blueBorder}`,
          borderRadius: L.radius,
          background: PANEL.sunken,
          color: PANEL.text,
          padding: '5px 8px',
          fontSize: PANEL.size.line,
        }}
      />

      <Del onClick={onRemove} label="Remove this line" />
    </div>
  );
}

// ── one editable instruction ────────────────────────────────────────────────
//
// NO DROPDOWN, and NO SEPARATE EDIT BUTTON.
//
// An instruction cannot become a role: instructions come from the page's
// printed headings and live in their own array, because `instructions_used`
// refers to them by id.
//
// The row is `[field][×]`, exactly like a role row minus the dropdown, so both
// fields end on the SAME right boundary. A third control here — an `edit full
// instruction` button — made that alignment impossible, so the truncated field
// IS the control: clicking it opens the whole thing.
function InstructionRow({ instruction, open, narrow, onOpen, onClose, onChange, onRemove, onRole }) {
  const mfRef = useRef(null);

  // AN INSTRUCTION IS A MATH-FIELD TOO, in the same text-first mode.
  //
  // It is the most prose-heavy content on the card — "Rewrite each equation in
  // exponential form." — so text mode is doing most of the work. But worksheet
  // headings routinely carry maths ("Solve for $x$ where $x > 0$"), and a
  // student who wants to correct one should not find a box that refuses.
  useEffect(() => {
    const mf = mfRef.current;
    if (!mf) return undefined;
    mf.defaultMode = 'text';
    mf.smartMode = true;
    mf.value = toMathValue(instruction);
    const onInput = () => onChange(fromMathValue(mf.value));
    mf.addEventListener('input', onInput);
    return () => mf.removeEventListener('input', onInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (open) {
    return (
      <div style={{ marginBottom: L.rowGap }}>
        <div style={{ display: 'flex', gap: L.gap, alignItems: 'flex-start' }}>
          {/* Open, it takes the full width and grows: while it is being edited
              it stops being a note and becomes the thing being worked on. */}
          <math-field
            ref={mfRef}
            style={{
              flex: 1, minWidth: 0, boxSizing: 'border-box',
              minHeight: 70,
              borderRadius: L.radius, background: PANEL.sunken,
              border: `0.5px solid ${PANEL.blueBorder}`,
              color: PANEL.text,
              fontSize: PANEL.size.instruction,
              padding: '8px 9px',
            }}
          />
          <Del onClick={onRemove} label="Remove this instruction" />
        </div>
        <div style={{ marginTop: 5 }}>
          <Btn onClick={onClose}>done</Btn>
        </div>
      </div>
    );
  }

  // COLLAPSED, BUT TYPABLE. It was a <button> — which looked exactly like a
  // field, was not one, and swallowed every keystroke. Pressing `edit` now puts
  // the caret in a real field straight away, with no second click to find.
  //
  // The row is still [field][×], so it lands on the same right boundary as
  // every role field. The expand control sits INSIDE the field rather than
  // beside it: a third element on the row is what made that alignment
  // impossible the first time.
  return (
    <div style={{ display: 'flex', gap: L.gap, alignItems: 'center', marginBottom: L.rowGap }}>
      {/* THE SAME DROPDOWN THE OTHER ROWS HAVE. The transcriber reads a problem
          as an instruction often enough — an unnumbered statement above a
          worksheet looks exactly like a directive — and without this the only
          repair was deleting the line and typing it back in. */}
      <select
        value="instruction"
        onChange={(e) => onRole && onRole(e.target.value)}
        aria-label="What this line is"
        style={{
          flex: `0 0 ${narrow ? L.roleWidthMobile : L.roleWidth}px`,
          height: L.fieldHeight, fontSize: PANEL.size.role,
          fontFamily: PANEL.sans, borderRadius: L.radius,
          background: PANEL.sunken,
          color: PANEL.secondary,
          border: `0.5px solid ${PANEL.border}`,
        }}
      >
        {MOVE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{narrow ? o.short : o.label}</option>
        ))}
      </select>

      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <math-field
          ref={mfRef}
          aria-label="Instruction"
          style={{
            width: '100%', boxSizing: 'border-box',
            minHeight: L.fieldHeight,
            border: `0.5px solid ${PANEL.border}`,
            borderRadius: L.radius,
            background: PANEL.sunken,
            color: PANEL.text,
            fontSize: PANEL.size.field,
            // Room for the expand control, which sits over the field's right end.
            padding: '4px 26px 4px 8px',
          }}
        />
        <button
          type="button"
          onClick={onOpen}
          aria-label="Edit the full instruction"
          title="Edit the full instruction"
          style={{
            position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
            width: 18, height: 18, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: 0, border: 'none',
            background: 'transparent', color: PANEL.muted,
            cursor: 'pointer', fontSize: PANEL.size.field, lineHeight: 1,
          }}
        >
          ⋯
        </button>
      </div>
      <Del onClick={onRemove} label="Remove this instruction" />
    </div>
  );
}

/**
 * The worked steps, once `SHOW ALL STEPS` has returned them.
 *
 * TWO COLUMNS. The chain runs down the left as one column of mathematics, so
 * the eye can follow it from line to line without stepping over prose. Each
 * explanation sits to the right of the step it produced — a label on the move,
 * not a sentence in the way of it.
 *
 * The first step is the problem and carries no explanation, because nothing
 * came before it. Its right column is simply empty.
 */
function Steps({ steps, narrow }) {
  return (
    // INSET FROM BOTH EDGES. The chain is a quotation inside the card, not more
    // of the card — pulling it in from the sides is what says so without a box
    // or a rule around it.
    <div style={{
      marginTop: 14,
      paddingLeft: narrow ? L.stepsInsetMobile : L.stepsInset,
      paddingRight: narrow ? L.stepsInsetMobile : L.stepsInset,
    }}>
      {steps.map((step, i) => (
        <div key={i} style={{ padding: i === 0 ? '0 0 2px' : '12px 0 2px' }}>
          {/* ── THE RULE, WITH THE MOVE WRITTEN ON IT ────────────────────────
              The label sits ON the rule rather than beside the equation, so it
              reads in the order the move happened: this is what was done, and
              below it is what it produced.

              CENTRED, on the same axis as the equations, so the block reads
              down one line rather than as a column of maths with a column of
              prose alongside.

              NOT ON THE FIRST STEP. It is the problem, and nothing was done to
              arrive at it.

              A long label leaves two short stubs of rule either side. The
              prompt asks for two or three words for exactly this reason. */}
          {i > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center',
              // NO GAP WITHOUT A LABEL. The prompt requires an explanation on
              // every entry after the first, but a model that omits one would
              // leave two rules with a space between them — a broken line
              // rather than an unbroken one. Zero here makes them meet.
              gap: step.explanation ? (narrow ? 9 : 11) : 0,
              marginBottom: 6,
            }}>
              <span style={{ flex: 1, borderTop: `0.5px solid ${PANEL.border}` }} />
              {step.explanation && (
                <span style={{
                  flex: '0 0 auto',
                  fontFamily: PANEL.sans,
                  // CAPS READ LARGER than lowercase at the same size, so this
                  // sits below the equation's size rather than beside it. The
                  // tracking and weight are what the headings and the three
                  // buttons already use.
                  fontSize: narrow ? 9.5 : 10,
                  letterSpacing: '0.08em',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  color: PANEL.muted,
                }}>
                  {step.explanation}
                </span>
              )}
              <span style={{ flex: 1, borderTop: `0.5px solid ${PANEL.border}` }} />
            </div>
          )}

          <MathLine
            element={{ kind: 'math', content: step.content }}
            size={narrow ? PANEL.size.lineMobile : PANEL.size.line}
            centre
          />
        </div>
      ))}
    </div>
  );
}

/**
 * One instruction, as a dashed note clipped short of the edge.
 *
 * The dash makes several instructions read as notes rather than as prose, and
 * stopping short is what keeps them looking that way — a line running to the
 * edge reads as a paragraph however it is punctuated.
 */
function InstructionNote({ text, notes, muted }) {
  return (
    <div style={{ display: 'flex', gap: L.gap, paddingRight: notes, margin: '0 0 1px' }}>
      <span aria-hidden="true" style={{ flex: '0 0 auto', color: PANEL.muted,
        fontFamily: PANEL.serif, fontSize: PANEL.size.instruction, lineHeight: 1.7 }}>
        —
      </span>
      <span title={text} style={{ overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', color: muted ? PANEL.muted : PANEL.secondary,
        fontFamily: muted ? PANEL.sans : PANEL.serif,
        fontSize: muted ? PANEL.size.hint : PANEL.size.instruction,
        lineHeight: muted ? 1.9 : 1.7 }}>
        {text}
      </span>
    </div>
  );
}

/**
 * The solution entry for a part.
 *
 * ONE HELPER, EVERY READ. The bucket, the task line and the steps all come from
 * the same entry, and three separate lookups would be three places to forget the
 * fallbacks.
 *
 * `|| list[0]` covers a STALE `activePart`: a resubmit can change which parts
 * exist, and a tab pointing at a part that has gone must land somewhere rather
 * than blank the answer.
 *
 * `|| null` covers no answer and an empty solution, so every caller can read
 * `entryFor(...)?.field` without a guard of its own.
 */
function entryFor(answer, part) {
  const list = (answer && answer.solution) || [];
  return list.find((s) => s.part === part) || list[0] || null;
}

// ── WHICH BUTTONS EACH BUCKET SHOWS ─────────────────────────────────────────
//
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  FILL THIS IN HERE. This table is the whole mapping — bucket in, buttons  ║
// ║  out. It is HARDCODED to give all three to every bucket, so the branch    ║
// ║  exists and only its contents change.                                     ║
// ║                                                                           ║
// ║  Replace ACTIONS_FOR_BUCKET below with the real per-bucket sets. The       ║
// ║  twelve buckets live in the server's services/buckets.js.                  ║
// ║                                                                           ║
// ║  TWO CASES THAT ARE NOT BUCKETS AND STILL ARRIVE HERE:                     ║
// ║    'CONCEPT'  the classifier's own FALLBACK_BUCKET when it fails twice.    ║
// ║              A real bucket, reached two ways — a genuine concept question  ║
// ║              and a classifier failure. They are indistinguishable here.    ║
// ║    null      a THROWN error in answerFor. It returns a fully shaped answer ║
// ║              with a null bucket, deliberately, so one bad problem does not ║
// ║              leave its card pending while its neighbours resolve.          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

const ACTIONS = [
  { id: 'steps', label: 'SHOW ALL STEPS' },
  { id: 'teach', label: 'TEACH ME' },
  { id: 'check', label: 'CHECK MY WORK' },
];

// bucket -> the action ids it offers. HARDCODED: everything gets everything.
function actionsForBucket(bucket) {
  return ACTIONS;      // ← the mapping goes here
}

// ── the card ────────────────────────────────────────────────────────────────

export default function ProblemCard({
  label,
  status,                 // 'answered' | 'editing' | 'pending'
  problem,                // what was SENT. read only when NOT editing
  draft,                  // the working copy. read ONLY when editing
  answer,                 // null unless answered
  showAnswerSection,      // ANSWER_TIERS (pro_plus). Pro would get the card without it, but pro is hidden for now
  labels,
  statusFor,
  activeLabel,
  onSelect,
  onEdit,
  onChangeElement,        // (index, patch)
  onRoleElement,          // (index, role)
  onPartElement,          // (index, part) — moves a line between parts
  onRemoveElement,        // (index)
  onAddElement,           // (role, part)
  onChangeInstruction,    // (id, content)
  onInstructionRole,      // (id, role) — moves it out of instructions
  onRemoveInstruction,    // (id)
  onAddInstruction,
  draftCount,
  onResubmit,
  clearExplained,          // a counter — when it changes, empty the store
  onExplain,               // (actionId) — placeholder, logs the response
}) {
  const narrow = useNarrow();
  const editing = status === 'editing';
  const typed = label === 'question';

  // Which instruction is open in full. One at a time — two open textareas push
  // everything else off screen and neither is obviously the one in hand.
  const [openInstruction, setOpenInstruction] = useState(null);
  useEffect(() => { if (!editing) setOpenInstruction(null); }, [editing]);

  // The answer is hidden until asked for. Keyed on the answer itself, so a
  // RESUBMIT hides it again — the student asked us to look afresh, and the new
  // result should not appear before they have asked to see it.
  // ── WHAT THE THREE BUTTONS BROUGHT BACK ───────────────────────────────────
  //
  // CARD LOCAL, and THE ENTIRE RESPONSE each time — not a field pulled out of
  // it, not the `Answer` half, the whole body exactly as the route sent it.
  //
  // WHY THE WHOLE THING. What each action returns is not settled: `teach` has
  // no slot in the contract at all, `check` fills a different part of the
  // answer than `steps` does, and any of them may grow a field that has no home
  // yet. Storing the response entire means a future feature reads something new
  // out of what is already held, rather than needing this to be rewritten to
  // let it through.
  //
  // Each display then takes what IT needs from the response it owns.
  //
  // At most three per problem — one per button — and a second press does
  // nothing, so the ceiling is three responses and three requests for a problem
  // however many times the buttons are clicked.
  //
  // KEYED BY LABEL, `{ [label]: { [actionId]: response } }`. The card is ONE
  // component showing whichever problem is active — it is not keyed by label —
  // so a flat `{ [actionId]: response }` would show problem 10's steps under
  // problem 11.
  //
  // It dies with the panel: a new question remounts everything and these go.
  const [explained, setExplained] = useState({});
  const [running, setRunning] = useState(null);

  // EMPTIED WHEN THE PANEL SAYS SO. `clearExplained` is a counter the panel
  // bumps on a resubmit and on a new question — the two places staleness is
  // known, both of which live up there rather than here.
  //
  // THE WHOLE STORE, not this label. A resubmit carries every draft, so results
  // for any problem may now describe working that has changed, and the card
  // knows only its own label.
  useEffect(() => { setExplained({}); }, [clearExplained]);

  const mine = explained[label] || {};

  const runAction = async (actionId) => {
    // ALREADY HAVE IT, OR ALREADY ASKING. A second press does nothing rather
    // than spending another model call on a response we are holding.
    if (mine[actionId] || running) return;
    if (!onExplain) return;

    setRunning(actionId);
    try {
      const response = await onExplain(actionId);
      // STORED WHOLE, whatever shape it has. No field is picked out here.
      if (response) {
        setExplained((prev) => ({
          ...prev,
          [label]: { ...(prev[label] || {}), [actionId]: response },
        }));
      }
    } catch (err) {
      console.error('[card] explain failed', actionId, err);
    } finally {
      setRunning(null);
    }
  };

  const source = editing ? draft : problem;
  const allInstructions = (source && source.instructions) || [];
  const elements = (source && source.elements_array) || [];

  // WHEN THE ROTATING MESSAGE RUNS: the interpretation is on screen and the
  // answer is not. Stated as that rather than as `status === 'pending'` —
  // they agree today, but the message is about what is VISIBLE, not about what
  // the log says.
  const awaitingAnswer = elements.length > 0 && !answer && !editing;
  const { phrase } = usePhases(awaitingAnswer);

  // ── WHICH INSTRUCTIONS RENDER ─────────────────────────────────────────────
  //
  // ALL OF THEM. NO CONDITION.
  //
  // The card shows exactly what is in the log. There was a filter here keyed on
  // `answer.instructions_used`, and it emptied the section whenever `answer` was
  // null — which is the whole of every resubmit, for every problem. The data was
  // sitting in `problem.instructions` the entire time and the card threw it
  // away.
  const visibleInstructions = allInstructions;

  // ── THE ACTIVE PART ───────────────────────────────────────────────────────
  //
  // Which tab is showing, or null on a problem that asks only one thing. The
  // tab strip governs the ANSWER and EXPLANATION sections only; the
  // interpretation above stays whole, because seeing every part together is the
  // reason they were merged into one problem.
  const partTabs = (answer && answer.solution || [])
    .map((s) => s.part)
    .filter((p) => p);
  const [activePart, setActivePart] = useState(null);

  // RESET WHEN THE ANSWER CHANGES. A resubmit can rename or remove parts, so a
  // tab held from the previous answer would point at nothing. Null falls to the
  // first entry via `entryFor`.
  useEffect(() => { setActivePart(null); }, [answer]);

  // The bucket decides the buttons. It lives inside the answer, so this is null
  // until the answer returns — which is exactly when the section appears.
  const entry = entryFor(answer, activePart);
  const bucket = (entry && entry.bucket) || null;
  const task = (entry && entry.task) || null;
  const buttons = answer ? actionsForBucket(bucket) : [];

  // The worked steps, if SHOW ALL STEPS has been pressed and come back. They
  // live where the server put them — inside the answer — so nothing here has
  // to hold a second copy.
  // THE STEPS DISPLAY TAKES WHAT IT NEEDS from the response `steps` stored.
  // The response is `{ Problem, Answer }`, so the steps are one level deeper
  // than they are on the `answer` prop — and the rest of that response stays
  // held, untouched, for whatever reads it next.
  //
  // AND PER PART. One press of SHOW ALL STEPS returns a chain for every part,
  // so the tab picks which to show and no second request is made.
  const stepsSource = (mine.steps && mine.steps.Answer) || answer;
  const stepsEntry = entryFor(stepsSource, activePart);
  const steps = (stepsEntry && stepsEntry.possible_correct_steps) || [];

  const pad = narrow ? L.cardPadMobile : L.cardPadX;
  const indentSection = narrow ? L.indentMobileSection : L.indentSection;
  const indentRole = narrow ? L.indentMobileRole : L.indentRole;
  const notes = narrow ? L.notesMobile : L.notes;

  const rolesPresent = ROLE_ORDER.filter((r) => elements.some((e) => roleOf(e) === r));

  // ── THE EDITOR'S PART GROUPS ──────────────────────────────────────────────
  //
  // The parts this problem has, and its rows bucketed into them IN PAGE ORDER.
  // Indices rather than elements, because every handler downstream addresses a
  // line by its index in `elements_array`.
  //
  // A PROBLEM WITH NO PARTS PRODUCES ONE GROUP keyed null, so the editor
  // renders exactly what it always did — one run of rows and one `+ line`.
  const editorParts = partsOf({ label, elements_array: elements });
  const editorGroups = (() => {
    if (!editorParts.length) return [{ part: null, indices: elements.map((_, i) => i) }];
    const shared = { part: null, indices: [] };
    const byPart = new Map(editorParts.map((p) => [p, { part: p, indices: [] }]));
    elements.forEach((el, i) => {
      const g = el.label && byPart.has(el.label) ? byPart.get(el.label) : shared;
      g.indices.push(i);
    });
    // The shared lines first — the problem statement sits above its parts on
    // the page, and the editor should not reorder what the student is reading.
    return [shared, ...byPart.values()].filter((g) => g.indices.length || g.part);
  })();

  // ── INSTRUCTIONS, SPLIT BY WHERE THEY TAKE EFFECT ─────────────────────────
  //
  // `fromStep` null means general to the whole problem; anything else names the
  // part it takes effect from. Each group is capped separately below.
  const generalInstructions = visibleInstructions.filter((i) => !i.fromStep);
  const partInstructions = (() => {
    const byPart = new Map();
    for (const ins of visibleInstructions) {
      if (!ins.fromStep) continue;
      if (!byPart.has(ins.fromStep)) byPart.set(ins.fromStep, { part: ins.fromStep, items: [] });
      byPart.get(ins.fromStep).items.push(ins);
    }
    return [...byPart.values()];
  })();

  // Whether to mark each line with its part. `partsOf` returns [] for a problem
  // with fewer than two parts, so an ordinary card gets no markers at all.
  const partTags = partsOf({ label, elements_array: elements }).length > 0;

  return (
    // TWO NESTED SURFACES. A wrapper one step darker than the card. The card
    // floating on a slightly different background is most of the effect.
    <div style={{
      background: PANEL.bg, borderRadius: L.cardRadius,
      padding: narrow ? 12 : 16, marginBottom: 16,
    }}>
      <div style={{
        background: PANEL.surface,
        border: `0.5px solid ${PANEL.border}`,
        borderRadius: L.cardRadius,
        padding: narrow ? `${pad}px` : `${L.cardPadY}px ${pad}px`,
        // The last defence for the right edge: nothing escapes the card even if
        // a field miscalculates.
        overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <p style={{ fontSize: PANEL.size.title, fontWeight: 500, margin: 0,
              fontFamily: PANEL.sans, color: PANEL.text }}>
              {/* Two RESERVED labels a worksheet problem can never carry. */}
              {typed ? 'Your question'
                : label === 'work' ? 'Your working'
                : `Problem ${label}`}
            </p>
            {editing && (
              <span style={{ fontSize: PANEL.size.badge, fontFamily: PANEL.sans,
                background: PANEL.accentBg, color: PANEL.accentText,
                padding: '1px 7px', borderRadius: L.radius }}>
                edited
              </span>
            )}
          </div>
          <Toggles labels={labels} statusFor={statusFor} current={activeLabel} onSelect={onSelect} />
        </div>

        {/* ── INTERPRETATION ─────────────────────────────────────────────── */}
        <Heading>Interpretation</Heading>
        <Hint>
          {editing
            ? 'Fix anything we misread, then resubmit.'
            : 'What we read from your page. If any of it is wrong, the answer will be too — press edit to correct it.'}
        </Hint>

        {editing ? (
          <>
            <Section title="Instructions" indentSection={indentSection} indentRole={indentRole}>
              {allInstructions.map((ins) => (
                <InstructionRow
                  key={ins.id}
                  instruction={ins}
                  open={openInstruction === ins.id}
                  onOpen={() => setOpenInstruction(ins.id)}
                  onClose={() => setOpenInstruction(null)}
                  onChange={(content) => onChangeInstruction(ins.id, content)}
                  onRemove={() => onRemoveInstruction(ins.id)}
                  onRole={(role) => onInstructionRole && onInstructionRole(ins.id, role)}
                  narrow={narrow}
                />
              ))}
              <Btn kind="grey" onClick={onAddInstruction}>+ instruction</Btn>
            </Section>

            {/* EVERY ROLE IS PRESENT IN EDIT, even with nothing in it. An empty
                section is an invitation to add a line, not clutter — which is
                the opposite of the display rule, deliberately. */}
            {/* ── SECTIONS BY ROLE, GROUPS BY PART ──────────────────────────
                The sections stay what they always were — Problem, Your work,
                Your answer. INSIDE each one the rows group by part, and every
                group ends with its own `+ line`.

                So `+ line` under 7a in Your work adds a WORK line to 7a. Two
                dimensions, both explicit, neither guessed.

                A PROBLEM WITH NO PARTS HAS ONE GROUP — `editorGroups` returns a
                single entry keyed null — so it renders exactly one `+ line` per
                section, which is what it always had. */}
            {ROLE_ORDER.map((role) => (
              <Section key={role} title={ROLE_TITLES[role]} deep
                indentSection={indentSection} indentRole={indentRole}>
                {editorGroups.map((group) => (
                  <div key={group.part == null ? '_shared' : group.part}>
                    {group.indices.map((i) => (roleOf(elements[i]) !== role ? null : (
                      // THE SAME KEY AS THE DISPLAY MAP: `kind`. A graphic is a
                      // grid or a description, not a line of maths, and
                      // `ElementField` would round-trip its prose through
                      // `fromMathValue` and corrupt it.
                      //
                      // NOTE THE TWO onChange SHAPES. `ElementField` hands back
                      // a STRING, wrapped into a patch here. `GraphicField`
                      // hands back the PATCH — a cell edit is `{ graphic }`, a
                      // description edit is `{ content }` — because
                      // `changeElement` spreads whatever it is given.
                      elements[i].kind === 'graphic' ? (
                        <GraphicField
                          key={elements[i]._uid != null ? elements[i]._uid : i}
                          element={elements[i]}
                          narrow={narrow}
                          parts={editorParts}
                          onChange={(patch) => onChangeElement(i, patch)}
                          onPart={(p) => onPartElement(i, p)}
                          onRemove={() => onRemoveElement(i)}
                        />
                      ) : (
                      <ElementField
                        // KEYED ON _uid, NOT INDEX. A math-field's value is set
                        // once on mount; with index keys, removing a line makes
                        // React reuse the node and the field keeps the removed
                        // line's content. _uid is added when the draft is seeded
                        // and stripped before sending.
                        key={elements[i]._uid != null ? elements[i]._uid : i}
                        element={elements[i]}
                        narrow={narrow}
                        parts={editorParts}
                        onChange={(content) => onChangeElement(i, { content })}
                        onRole={(r) => onRoleElement(i, r)}
                        onPart={(p) => onPartElement(i, p)}
                        onRemove={() => onRemoveElement(i)}
                      />
                      )
                    )))}
                    {/* ADDS TO THIS PART, IN THIS ROLE. The group's own button,
                        so a student never has to say afterwards which part they
                        meant. */}
                    <Btn onClick={() => onAddElement(role, group.part)}>+ line</Btn>
                  </div>
                ))}
              </Section>
            ))}
          </>
        ) : (
          <>
            {/* HIDDEN WHEN THERE ARE NONE, exactly as the roles below are.
                A student whose page carried no printed heading should not see a
                heading over nothing.

                SAFE NOW, AND IT WAS NOT BEFORE. This is the same length gate
                that emptied the section on every resubmit — but back then
                `visibleInstructions` was filtered by `answer.instructions_used`,
                so it went empty whenever there was no answer, which is the whole
                of every pending window. It is `allInstructions` now, so an empty
                array means the problem genuinely has no instructions. */}
            {visibleInstructions.length > 0 && (
            <Section title="Instructions" indentSection={indentSection} indentRole={indentRole}>
                <div style={{ marginLeft: L.indentBody }}>
                  {/* ── GENERAL FIRST, THEN EACH PART ──────────────────────────
                      SEPARATE CAPS. Three general rules, then three more per
                      part. One shared cap would hide a part's own rule behind
                      the problem's general ones — and a rule written for part c
                      is the one most likely to have changed the answer.

                      A problem with no parts has one group and behaves exactly
                      as it did before. */}
                  {generalInstructions.slice(0, MAX_SHOWN_INSTRUCTIONS).map((ins) => (
                    <InstructionNote key={ins.id} text={ins.content} notes={notes} />
                  ))}
                  {generalInstructions.length > MAX_SHOWN_INSTRUCTIONS && (
                    <InstructionNote muted notes={notes}
                      text={`${generalInstructions.length - MAX_SHOWN_INSTRUCTIONS} more`} />
                  )}

                  {partInstructions.map((group) => (
                    // INDENTED UNDER ITS PART. The part name is the heading of a
                    // small group, not a marker on each line, because every line
                    // in the group shares it.
                    <div key={group.part} style={{ marginTop: 6, marginLeft: L.indentBody }}>
                      <span style={{ fontFamily: PANEL.sans, fontSize: PANEL.size.toggle,
                        letterSpacing: '0.06em', color: PANEL.muted }}>
                        {group.part}
                      </span>
                      {group.items.slice(0, MAX_SHOWN_INSTRUCTIONS).map((ins) => (
                        <InstructionNote key={ins.id} text={ins.content} notes={notes} />
                      ))}
                      {group.items.length > MAX_SHOWN_INSTRUCTIONS && (
                        <InstructionNote muted notes={notes}
                          text={`${group.items.length - MAX_SHOWN_INSTRUCTIONS} more`} />
                      )}
                    </div>
                  ))}
                </div>
            </Section>
            )}

            {/* EMPTY ROLES ARE HIDDEN IN DISPLAY. A student who has not started
                should not see two headings over nothing. */}
            {rolesPresent.map((role) => (
              <Section key={role} title={ROLE_TITLES[role]} deep
                indentSection={indentSection} indentRole={indentRole}>
                <div style={{ marginLeft: L.indentBody }}>
                  {elements.map((el, i) => (roleOf(el) !== role ? null : (
                    // ── WHICH PART THIS LINE BELONGS TO ────────────────────
                    //
                    // Grouping by ROLE alone runs three questions together with
                    // nothing saying which is which — the reported bug, moved
                    // inside one card. The part is on the element; this is the
                    // last moment before it would be thrown away.
                    //
                    // WRAPPED, NOT PASSED INTO MathLine. `renderMathIn` writes
                    // over that element's contents, so a marker inside it would
                    // be erased on every render.
                    //
                    // ONLY ON A PROBLEM THAT HAS PARTS, and never on the stem.
                    // An ordinary card renders exactly the line it always did.
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                      {partTags && el.label && el.label !== label && (
                        <span style={{
                          flex: '0 0 auto',
                          fontFamily: PANEL.sans,
                          fontSize: PANEL.size.toggle,
                          letterSpacing: '0.06em',
                          color: PANEL.muted,
                          lineHeight: 1.75,
                        }}>
                          {el.label}
                        </span>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* ONE KEY DECIDES: `kind`. Not origin, not pairId, not
                            type. A graphic is a block — a grid or a described
                            box — and MathLine typesets a LINE, so a graphic
                            sent through it renders its description as maths.

                            A description reading "Cash Flow Today ($)" has TWO
                            dollar signs in it, and everything between them was
                            being set as an expression. */}
                        {el.kind === 'graphic'
                          ? <Graphic element={el} narrow={narrow} />
                          : <MathLine element={el} narrow={narrow} />}
                      </div>
                    </div>
                  )))}
                </div>
              </Section>
            ))}

            <div style={{ textAlign: 'right', marginTop: 11 }}>
              <Btn kind="edit" onClick={onEdit}>edit</Btn>
            </div>
          </>
        )}

        {/* ── ANSWER ──────────────────────────────────────────────────────────
            Hidden the moment the editor opens — not when content differs. No
            comparison runs while the student types, so nothing flickers.

            ANSWER_TIERS only (pro_plus). Pro does not currently see the card at
            all — PANEL_TIERS is pro_plus only for now. When pro returns it gets
            the card without this section; the resubmit is still worth something
            to them, because `search_terms` will drive which lessons surface once
            enrichment is switched back on. */}
        {showAnswerSection && !editing && (awaitingAnswer || answer) && (
          <div style={{ borderTop: `0.5px solid ${PANEL.border}`, marginTop: 15, paddingTop: 12 }}>
            {/* THE HEADING AND THE PART TABS SHARE A ROW. `Toggles` hides itself
                below two entries, so a problem that asks one thing gets exactly
                what it got before — the heading alone, in the same place.

                THE SAME COMPONENT AS THE PROBLEM STRIP at the top of the card,
                because it is the same gesture. A second one built to look like
                it would be a second thing to keep in step.

                `statusFor` RETURNS A CONSTANT. It exists to colour a tab by
                whether its problem is answered, pending or being edited — and
                parts do not have independent statuses, they share the problem's.
                Every part tab therefore looks the same, which is honest. */}
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: 10 }}>
              <Heading>Answer</Heading>
              <Toggles
                labels={partTabs}
                statusFor={() => 'answered'}
                current={activePart || partTabs[0]}
                onSelect={setActivePart}
              />
            </div>
            <Hint>
              {awaitingAnswer
                ? 'Working this out from the interpretation above.'
                // NOT "based on the interpretation above" any more — there is no
                // answer under it to be based on anything. It names what we read
                // the question to be asking, which is what the task line shows.
                : 'What we read the question as asking. Ask for more below.'}
            </Hint>

            {awaitingAnswer ? (
              <div style={{ marginTop: 6 }}>
                <p key={phrase} className="pc-phrase" style={{
                  fontSize: PANEL.size.field, margin: '0 0 9px',
                  color: PANEL.secondary, fontFamily: PANEL.sans,
                }}>
                  {phrase}
                </p>
                {/* FILL on a worksheet problem, SWEEP on a typed one: after
                    transcription several classifier calls are in flight at
                    once, and the quicker rhythm says so. */}
                <Bar style={typed ? 'sweep' : 'fill'} />
              </div>
            ) : (
              <>
                {/* THE TASK LINE COMES FROM THE SOLUTION ENTRY, not from
                    `problem_interpretation`. `understood` is per part now — a
                    problem where 7a finds a height and 7b finds a different one
                    is not doing ONE task, so a single line for the problem
                    would be wrong on at least one tab.

                    Stage 4 makes this read the ACTIVE part. Until then it reads
                    the first entry, which is what the card showed before. */}
                {task && (
                  <p style={{ fontFamily: PANEL.serif, fontSize: PANEL.size.field,
                    margin: '6px 0 8px', color: PANEL.secondary }}>
                    {task}
                  </p>
                )}
                {/* ── NO ANSWER SHOWN ────────────────────────────────────────
                    The value and its SHOW ANSWER button are both gone.
                    `llm_guessed_answer` was consistently wrong — the classifier
                    was asked for a final answer with "no working, no
                    explanation", as the last field of a large JSON object, so
                    it had nowhere to do the arithmetic. `SHOW ALL STEPS` gets
                    the same problems right because it is allowed to write a
                    chain.

                    The field still travels as null. What replaces it here is
                    not decided — a verified `solver_answer`, or the last entry
                    of the steps chain. */}

                {buttons.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <Heading>Explanation</Heading>
                    {/* CENTRED AND EQUAL-WIDTH, not a left-aligned row of
                        controls. These are the card's only outward doors —
                        everything else on it edits or resends what is already
                        there. They should not read as siblings of `+ line`.

                        GREEN. Blue is the panel's furniture — headings, field
                        borders, `+ line`. Gold means Resubmit. Green already
                        means `edit`, which is the other control that OPENS
                        something rather than sending it, so these read as its
                        siblings. */}
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      gap: narrow ? 6 : 8,
                      marginTop: 10,
                    }}>
                      {buttons.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => runAction(a.id)}
                          disabled={!!running}
                          style={{
                            // EQUAL WIDTH ON A DESKTOP. `1 1 0` divides the row
                            // evenly and ignores the labels; `1 1 auto` would
                            // size each to its own text, so "teach me" would sit
                            // narrower than the other two.
                            //
                            // FULL WIDTH ON A PHONE. Three across 331px is about
                            // 103px each, which wraps "show me the steps" onto
                            // three lines. Stacked, each label stays on one.
                            flex: narrow ? '1 1 100%' : '1 1 0',
                            minWidth: 0,
                            padding: narrow ? '10px 12px' : '11px 16px',
                            borderRadius: L.radius,
                            background: PANEL.successBg,
                            color: PANEL.successText,
                            border: `0.5px solid ${PANEL.successBorder}`,
                            fontFamily: PANEL.sans,
                            fontSize: narrow ? 12.5 : 13,
                            // ALL CAPS NEEDS TRACKING. Set solid, capitals crowd
                            // each other — there are no descenders or x-height
                            // changes to separate them.
                            letterSpacing: '0.06em',
                            fontWeight: 500,
                            lineHeight: 1.2,
                            cursor: running ? 'default' : 'pointer',
                            // SPENT. It has been pressed and its result is on
                            // screen, so pressing it again would do nothing —
                            // the button says so rather than looking live.
                            opacity: mine[a.id] ? 0.55 : 1,
                            transition: 'background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease',
                          }}
                        >
                          {running === a.id ? 'WORKING…' : a.label}
                        </button>
                      ))}
                    </div>

                    {/* BELOW THE BUTTONS. The buttons are the doors; what comes
                        back sits under the one that was opened, not above it. */}
                    {steps.length > 0 && <Steps steps={steps} narrow={narrow} />}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── RESUBMIT ────────────────────────────────────────────────────────
            Visible whenever ANYTHING is unsent, not only while this card is in
            edit mode — otherwise a student who edits 7 and toggles to 10 has no
            way to send. The count comes from `drafts`, which is why it knows
            about problems they cannot currently see.

            It ALWAYS sends, changed or not. Open the editor and the answer is
            gone; Resubmit brings it back, with no exceptions to the rule. */}
        {draftCount > 0 && (
          <div style={{
            borderTop: `0.5px solid ${PANEL.border}`, marginTop: 15, paddingTop: 12,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: PANEL.size.hint, color: PANEL.secondary,
              fontFamily: PANEL.sans }}>
              {draftCount} problem{draftCount === 1 ? '' : 's'} changed
            </span>
            <div style={{ marginLeft: 'auto' }}>
              <Btn kind="resub" onClick={onResubmit}>Resubmit</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
